@if (visible && uploads.length > 0) {
  <section class="upload-panel" role="region" aria-label="Upload progress">
    <!-- Live region for screen reader announcements -->
    <div class="sr-only" aria-live="polite">{{ statusText }}</div>

    <!-- Collapsed header bar -->
    <ion-row>
      <ion-col size="12" sizeMd="8" offsetMd="2" sizeLg="6" offsetLg="3" class="lp-padding-none">
        <ion-item
          class="lp-padding-horizontal-8"
          lines="none"
          button
          [detail]="false"
          [attr.aria-expanded]="expanded"
          aria-controls="upload-file-list"
          (click)="toggleExpanded()">
          <ion-icon
            slot="start"
            [name]="allComplete && failedCount === 0 ? 'checkmark-circle' : 'cloud-upload'"
            [color]="allComplete && failedCount === 0 ? 'success' : 'primary'"
            aria-hidden="true"></ion-icon>
          <ion-label>
            <ion-text class="status-text">{{ statusText }}</ion-text>
          </ion-label>
          @if (allComplete) {
            <ion-button
              slot="end"
              fill="clear"
              size="small"
              aria-label="Dismiss upload panel"
              (click)="dismiss(); $event.stopPropagation()">
              <ion-icon slot="icon-only" name="close" aria-hidden="true"></ion-icon>
            </ion-button>
          } @else {
            <ion-icon
              slot="end"
              [name]="expanded ? 'chevron-down' : 'chevron-up'"
              aria-hidden="true"
              size="small"
              color="medium"></ion-icon>
          }
        </ion-item>
      </ion-col>
    </ion-row>

    <!-- Overall progress bar (visible when collapsed) -->
    @if (!allComplete) {
      <ion-progress-bar
        [value]="overallProgress"
        color="primary"
        class="overall-progress"
        aria-label="Overall upload progress"
        [attr.aria-valuenow]="Math.round(overallProgress * 100)"
        aria-valuemin="0"
        aria-valuemax="100"></ion-progress-bar>
    }

    <!-- Expanded file list -->
    @if (expanded) {
      <div id="upload-file-list" class="upload-list">
        <!-- Retry all failed button -->
        @if (failedCount > 0) {
          <ion-row>
            <ion-col
              size="12"
              sizeMd="8"
              offsetMd="2"
              sizeLg="6"
              offsetLg="3"
              class="lp-padding-none">
              <div class="retry-all">
                <div class="lp-display-flex ion-align-items-center">
                  <ion-button
                    fill="clear"
                    size="small"
                    color="danger"
                    (click)="retryAllFailed()"
                    [attr.aria-label]="
                      'Retry ' + failedCount + ' failed upload' + (failedCount !== 1 ? 's' : '')
                    ">
                    <ion-icon slot="icon-only" name="refresh" aria-hidden="true"></ion-icon>
                  </ion-button>
                  <ion-text color="medium" class="lp-font-size-10">
                    Retry {{ failedCount }} failed upload{{ failedCount !== 1 ? 's' : '' }}
                  </ion-text>
                </div>
              </div>
            </ion-col>
          </ion-row>
        }

        <ion-row>
          <ion-col
            size="12"
            sizeMd="8"
            offsetMd="2"
            sizeLg="6"
            offsetLg="3"
            class="lp-padding-none">
            <ion-list class="file-list" lines="none">
              @for (item of uploads; track item.id ?? $index) {
                <ion-item class="file-row">
                  <ion-icon
                    slot="start"
                    [name]="getStatusIcon(item.status)"
                    [color]="getStatusColor(item.status)"
                    class="file-status-icon"
                    role="img"
                    [attr.aria-label]="getStatusLabel(item.status)"></ion-icon>

                  <ion-label class="file-info">
                    <span class="file-name">{{ item.metadata.fileName }}</span>
                    <span class="file-size">{{ formatSize(item.fileSize ?? item.file.size) }}</span>

                    @if (item.status === UploadStatus.Uploading) {
                      <ion-progress-bar
                        [value]="item.progress ?? 0"
                        color="primary"
                        class="file-progress"
                        [attr.aria-label]="'Upload progress for ' + item.metadata.fileName"
                        [attr.aria-valuenow]="Math.round((item.progress ?? 0) * 100)"
                        aria-valuemin="0"
                        aria-valuemax="100"></ion-progress-bar>
                    }

                    @if (item.status === UploadStatus.Failed) {
                      <ion-text
                        color="danger"
                        class="error-text"
                        role="alert"
                        [id]="'error-' + (item.id ?? $index)">
                        @if ((item.retryCount ?? 0) >= (item.maxRetries ?? 3)) {
                          Max retries reached
                        } @else {
                          {{ item.errorMessage || 'Upload failed' }}
                        }
                      </ion-text>
                    }
                  </ion-label>

                  <div slot="end" class="file-actions">
                    @if (item.status === UploadStatus.Failed) {
                      @if ((item.retryCount ?? 0) < (item.maxRetries ?? 3)) {
                        <ion-button
                          class="lp-margin-end-4"
                          fill="clear"
                          size="small"
                          color="primary"
                          [attr.aria-label]="'Retry upload for ' + item.metadata.fileName"
                          [attr.aria-describedby]="'error-' + (item.id ?? $index)"
                          (click)="retryUpload(item)">
                          <ion-icon slot="icon-only" name="refresh" aria-hidden="true"></ion-icon>
                        </ion-button>
                      }
                      <ion-button
                        fill="clear"
                        size="small"
                        color="medium"
                        [attr.aria-label]="'Remove failed upload for ' + item.metadata.fileName"
                        (click)="removeUpload(item)">
                        <ion-icon slot="icon-only" name="close" aria-hidden="true"></ion-icon>
                      </ion-button>
                    }

                    @if (
                      item.status === UploadStatus.Uploading || item.status === UploadStatus.Pending
                    ) {
                      <ion-button
                        fill="clear"
                        size="small"
                        color="medium"
                        [disabled]="cancellingIds.has(item.id!)"
                        [attr.aria-label]="'Cancel upload for ' + item.metadata.fileName"
                        (click)="cancelUpload(item)">
                        <ion-icon slot="icon-only" name="close" aria-hidden="true"></ion-icon>
                      </ion-button>
                    }
                  </div>
                </ion-item>
              }
            </ion-list>
          </ion-col>
        </ion-row>
      </div>
    }
  </section>
}
